import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useState } from 'react'
import { Loader2, FileText, ExternalLink, ChevronRight } from 'lucide-react'
import { PortalPageHeader } from './ui/PortalPageHeader'
import { PortalEmptyState } from './ui/PortalEmptyState'
import { PortalSkeletonList } from './ui/PortalSkeleton'

const LANG_LABELS: Record<string, string> = {
  en: 'English', fr: 'French', ar: 'Arabic',
  pt: 'Portuguese', sw: 'Swahili', ha: 'Hausa',
}

export function PortalPressReleaseView() {
  const qc = useQueryClient()
  const [selected, setSelected] = useState<any | null>(null)
  const [coverageUrl, setCoverageUrl] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const { data: releases, isLoading } = useQuery({
    queryKey: ['portal-press-releases'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []
      const { data, error } = await (supabase as any)
        .from('portal_press_releases')
        .select('*')
        .eq('portal_journalist_id', user.id)
        .order('sent_at', { ascending: false })
      if (error) throw error
      return (data || []) as any[]
    },
  })

  const markOpenedMutation = useMutation({
    mutationFn: async (recipientId: string) => {
      await supabase.rpc('mark_press_release_opened' as any, {
        p_recipient_id: recipientId,
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-press-releases'] }),
  })

  function openRelease(release: any) {
    setSelected(release)
    setCoverageUrl(release.coverage_url ?? '')
    if (!release.opened_at) markOpenedMutation.mutate(release.recipient_id)
  }

  async function submitCoverage() {
    if (!selected || !coverageUrl.trim()) return
    setSubmitting(true)
    try {
      await (supabase as any)
        .from('press_release_recipients')
        .update({
          coverage_url:           coverageUrl.trim(),
          coverage_submitted_at:  new Date().toISOString(),
        })
        .eq('id', selected.recipient_id)
      qc.invalidateQueries({ queryKey: ['portal-press-releases'] })
      setSelected(s => ({ ...s, coverage_url: coverageUrl.trim() }))
    } finally {
      setSubmitting(false)
    }
  }

  const unread = (releases || []).filter((r: any) => !r.opened_at).length

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">

      <PortalPageHeader
        title="Press Releases"
        description={
          unread > 0
            ? `Press releases sent to you by organisations you are accredited to. ${unread} unread.`
            : 'Press releases sent to you by organisations you are accredited to.'
        }
      />

      {isLoading ? (
        <PortalSkeletonList rows={4} />
      ) : !(releases || []).length ? (
        <PortalEmptyState
          icon={FileText}
          message="No press releases yet. Releases sent by organisations you are accredited to will appear here."
        />
      ) : selected ? (
        // Detail view
        <div>
          <button
            onClick={() => setSelected(null)}
            className="flex items-center gap-1 text-[12.5px] text-muted-foreground
                       hover:text-foreground mb-5 transition-colors"
          >
            ← Back to releases
          </button>

          <div className="bg-background border border-border rounded-xl p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10.5px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  {selected.org_name}
                </div>
                <h2 className="text-[16px] font-bold">{selected.title}</h2>
                <div className="flex items-center gap-3 mt-1 text-[11.5px] text-muted-foreground">
                  <span>{new Date(selected.sent_at).toLocaleDateString('en', {
                    day: 'numeric', month: 'long', year: 'numeric',
                  })}</span>
                  <span className="uppercase">{LANG_LABELS[selected.language] ?? selected.language}</span>
                  {(selected.tags || []).length > 0 && (
                    <span>{selected.tags.join(' · ')}</span>
                  )}
                </div>
              </div>
            </div>

            {selected.embargo_until && new Date(selected.embargo_until) > new Date() && (
              <div className="bg-[hsl(var(--warning)/0.08)] border border-[hsl(var(--warning)/0.3)] rounded-lg p-3
                              text-[12px] text-warning font-medium">
                ⚠ Embargoed until {new Date(selected.embargo_until).toLocaleString('en', {
                  dateStyle: 'long', timeStyle: 'short',
                })}
              </div>
            )}

            {selected.summary && (
              <p className="text-justify text-[13px] text-foreground font-medium border-l-4
                            border-accent pl-3">
                {selected.summary}
              </p>
            )}

            <div className="text-[13px] text-foreground leading-relaxed whitespace-pre-wrap">
              {selected.body}
            </div>

            {(selected.contact_name || selected.contact_email) && (
              <div className="bg-muted/30 rounded-xl p-3 text-[12px]">
                <p className="text-justify font-medium mb-0.5">Media Contact</p>
                {selected.contact_name && <p>{selected.contact_name}</p>}
                {selected.contact_email && (
                  <a href={`mailto:${selected.contact_email}`}
                     className="text-success hover:underline">
                    {selected.contact_email}
                  </a>
                )}
                {selected.contact_phone && <p className="text-justify text-muted-foreground">{selected.contact_phone}</p>}
              </div>
            )}

            {/* Coverage submission */}
            <div className="border-t border-border pt-4">
              <p className="text-justify text-[12.5px] font-medium mb-2">Submit Coverage</p>
              <p className="text-justify text-[12px] text-muted-foreground mb-3">
                Did you write a story based on this release? Submit the URL to earn
                coverage credit towards your score.
              </p>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={coverageUrl}
                  onChange={e => setCoverageUrl(e.target.value)}
                  placeholder="https://…"
                  className="flex-1 px-3 py-2 border border-border rounded-lg
                             text-[12.5px] bg-background focus:outline-none
                             focus:border-success"
                />
                <button
                  disabled={!coverageUrl.trim() || submitting}
                  onClick={submitCoverage}
                  className="px-4 py-2 bg-success text-success-foreground rounded-lg text-[12.5px]
                             font-medium hover:bg-success disabled:opacity-50
                             transition-colors"
                >
                  {submitting ? '…' : 'Submit'}
                </button>
              </div>
              {selected.coverage_url && (
                <div className="mt-2 flex items-center gap-1 text-[11.5px] text-success">
                  ✓ Coverage submitted:{' '}
                  <a href={selected.coverage_url} target="_blank" rel="noopener noreferrer"
                     className="hover:underline flex items-center gap-0.5">
                    view article <ExternalLink size={10} />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        // List view
        <div className="space-y-2">
          {(releases || []).map((r: any) => (
            <button
              key={r.recipient_id}
              onClick={() => openRelease(r)}
              className="w-full text-left bg-background border border-border rounded-xl
                         p-4 hover:bg-muted/20 transition-colors flex items-start justify-between gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  {!r.opened_at && (
                    <span className="inline-block w-2 h-2 rounded-full bg-success shrink-0" />
                  )}
                  <span className="text-[10.5px] font-medium text-muted-foreground uppercase tracking-wide">
                    {r.org_name}
                  </span>
                </div>
                <p className={`text-[13.5px] ${!r.opened_at ? 'font-semibold' : 'font-medium'} leading-snug`}>
                  {r.title}
                </p>
                {r.summary && (
                  <p className="text-justify text-[12px] text-muted-foreground line-clamp-1 mt-0.5">
                    {r.summary}
                  </p>
                )}
                <p className="text-justify text-[11px] text-muted-foreground mt-1">
                  {new Date(r.sent_at).toLocaleDateString('en', {
                    day: 'numeric', month: 'short', year: 'numeric',
                  })}
                  {r.coverage_url && (
                    <span className="ml-2 text-success">✓ Coverage submitted</span>
                  )}
                </p>
              </div>
              <ChevronRight size={16} className="text-muted-foreground shrink-0 mt-1" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
